import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccessToken, IDToken, OktaAuth } from '@okta/okta-auth-js';
import { Observable, Observer, BehaviorSubject } from 'rxjs';

import { EnvironmentVariablesService } from '../environment-variables.service';
import { IamService } from './iam.service';
import { SplitService } from '../split/split.service';
import { Logging } from '../../utils/logging';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  config = {
    clientId: this.environmentVariables.OKTA_CLIENT_ID,
    issuer: this.environmentVariables.OKTA_ISSUER,
    redirectUri: this.environmentVariables.OKTA_CALLBACK_PATH,
    scopes: ['openid', 'email', 'profile'],
    pkce: true,
  };
  oktaAuth = new OktaAuth(this.config);

  private hasBeenAuthenticated: BehaviorSubject<boolean>;
  public userName: BehaviorSubject<string>;
  public emailAddress: BehaviorSubject<string>;
  public userGuid: BehaviorSubject<string>;
  public accessToken: BehaviorSubject<string>;

  public tokenContainer: any;

  private alreadyLoggedInPath: boolean = false;
  private navigationAfterPermissions = '';

  $isAuthenticated: Observable<boolean>;
  private observer: Observer<boolean> | undefined;

  constructor(
    private environmentVariables: EnvironmentVariablesService,
    private IAM: IamService,
    private router: Router,
    private splitService: SplitService,
  ) {
    this.hasBeenAuthenticated = new BehaviorSubject<boolean>(false);
    this.userName = new BehaviorSubject<string>('');
    this.emailAddress = new BehaviorSubject<string>('');
    this.userGuid = new BehaviorSubject<string>('');
    this.accessToken = new BehaviorSubject<string>('');

    this.IAM.isObtainingPermission().subscribe(obtaining => {
      if (this.alreadyLoggedInPath && !obtaining) {
        this.router.navigateByUrl(this.navigationAfterPermissions);
      }
    });

    this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
      this.observer = observer;
      this.navigationAfterPermissions =
        window.location.pathname +
        (window.location.search ? window.location.search : '');

      this.checkIfAuthenticated().then(value => {
        if (value) {
          this.oktaAuth.tokenManager.getTokens().then(val => {
            let t = val.accessToken.expiresAt;
            let MS_PER_MINUTE = 60000;

            let date = new Date(t * 1000 - 5 * MS_PER_MINUTE);
            if (date < new Date()) {
              this.login(this.navigationAfterPermissions);
            }

            this.hasBeenAuthenticated.next(true);
            this.userName.next(
              val.idToken.claims.name != ''
                ? val.idToken.claims.name
                : val.idToken.claims.email,
            );
            this.emailAddress.next(val.idToken.claims.email);
            this.userGuid.next(val.idToken.claims.user_guid.toString());
            this.accessToken.next(val.accessToken.accessToken);

            this.splitService.initialize(val.idToken.claims.email);

            this.alreadyLoggedInPath = true;
            this.IAM.getUserIAM(val.accessToken);
          });
        } else {
          this.login(this.navigationAfterPermissions);
        }
        observer.next(value);
      });
    });
  }

  isAuthenticated(): Observable<boolean> {
    return this.hasBeenAuthenticated.asObservable();
  }

  login(originalUrl: string) {
    const url =
      originalUrl || window.location.pathname + window.location.search;
    if (url.toLowerCase().includes('callback')) return;
    localStorage.setItem('okta-app-url', url);
    this.oktaAuth.signInWithRedirect({
      responseType: 'id_token',
    });
  }

  async handleAuthentication() {
    this.oktaAuth.token.parseFromUrl().then(t => {
      localStorage.setItem(
        'okta-token-storage',
        JSON.stringify(t.tokens.accessToken),
      );
      this.tokenContainer = t.tokens;

      this.oktaAuth.tokenManager.add(
        'idToken',
        this.tokenContainer.idToken as IDToken,
      );
      this.oktaAuth.tokenManager.add(
        'accessToken',
        this.tokenContainer.accessToken as AccessToken,
      );

      this.alreadyLoggedInPath = false;
      this.IAM.getUserIAM(this.tokenContainer.accessToken);

      const url = localStorage.getItem('okta-app-url');

      if (url && url.trim() !== '') {
        window.location.href = url;
      } else {
        window.location.href = '/';
      }
    });
    if ((await this.checkIfAuthenticated()) && this.observer) {
      this.observer.next(true);
    }
  }

  async logout() {
    this.oktaAuth.tokenManager.clear();

    await this.oktaAuth.session
      .close()
      .then(val => {})
      .catch(err => {
        Logging.error('error', err);
      });

    localStorage.removeItem('okta-cache-storage');
    localStorage.removeItem('okta-token-storage');
    localStorage.removeItem('okta-user-permissions');
    localStorage.removeItem('okta-pkce-storage');
    localStorage.removeItem('okta-user-name');
    this.deleteAllCookies();

    window.location.href = this.environmentVariables.HOST;
  }

  // TODO Does this work in the way we think it does?
  deleteAllCookies() {
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  }

  async checkIfAuthenticated() {
    // Checks if there is a current accessToken in the TokenManger.
    return !!(await this.oktaAuth.tokenManager.get('accessToken'));
  }
}
